// MACHINERY V2 (Stroje) — status badge + awaiting badge (M2.1).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §4.3 (LOCKED).
// Reference impl: design_handoff_machinery_v3/machinery-live.jsx (StatusBadge,
// AwaitingBadge).
//
// Colour AND icon, always — the badge never ships a bare colour swatch.
// `stale` is NOT a prop the caller invents: pass `staleLook` from
// deriveCardPresentation(). It is TRUE for the stale / stale+alarm states and
// deliberately FALSE for `offline` — offline is already the server's own
// staleness verdict, and desaturating it would strip the slate + cloud-off
// colour/icon pair that identifies it.
//
// Variants:
//   "chip"  — small inline pill (fleet cards, grouped rows)
//   "strip" — full-width band (big card header, M2.4 profile hero ribbon)
// The handoff's third "large" kiosk variant is NOT ported: the V4 shop-floor
// wall is out of scope (§4.2). Add it there if the wall is ever built.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import StatusIcon from "./StatusIcon";
import {
  MachineryV2Status,
  STATUS_SPECS,
  statusChipStyle,
  statusStripStyle,
} from "./cardStates";

export interface StatusBadgeProps {
  status: MachineryV2Status | null;
  /** Always presentation.staleLook — see the header note. */
  stale?: boolean;
  variant?: "chip" | "strip";
  /** Strip variant only: trailing "od 06:49 · 2h 14m" clause. */
  showSince?: string | null;
  /** Strip variant only: prefix the since clause as last-known data. */
  lastKnown?: boolean;
}

/** The stale marker: a superscript "?" next to the label. A second,
 *  non-colour signal that the reading is last-known, for the same
 *  colour-blind-safety reason the status carries an icon. */
const StaleMark: React.FC<{ size: number; top: number }> = ({ size, top }) => (
  <sup
    style={{
      fontSize: size,
      fontWeight: 700,
      marginLeft: 1,
      opacity: 0.9,
      top,
      position: "relative",
    }}
  >
    ?
  </sup>
);

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  stale = false,
  variant = "chip",
  showSince = null,
  lastKnown = false,
}) => {
  const { translate } = useTranslation("myMachinery");

  // Unconnected machines render NOTHING (§4.3) — no badge, no placeholder.
  if (!status) return null;

  const spec = STATUS_SPECS[status];
  const label = translate(spec.labelKey);

  if (variant === "strip") {
    return (
      <div style={statusStripStyle(status, stale)}>
        <StatusIcon kind={spec.icon} size={13} />
        <span>{label}</span>
        {stale && <StaleMark size={10} top={-3} />}
        {showSince && (
          <span
            style={{
              letterSpacing: "0.04em",
              textTransform: "lowercase",
              fontWeight: 500,
              opacity: 0.7,
            }}
          >
            {"· "}
            {lastKnown && stale
              ? "(" + translate("myMachinery:v2.lastKnown") + ") "
              : ""}
            {showSince}
          </span>
        )}
      </div>
    );
  }

  return (
    <span style={statusChipStyle(status, stale)}>
      <StatusIcon kind={spec.icon} size={11} />
      <span>{label}</span>
      {stale && <StaleMark size={9} top={-2} />}
    </span>
  );
};

/** State 2 of 9: a MachineConnection row exists but the agent has never sent
 *  data. Dashed module-teal outline — deliberately NOT one of the 5 status
 *  colours, because nothing has been reported yet. */
export const AwaitingBadge: React.FC = () => {
  const { translate } = useTranslation("myMachinery");
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        height: 22,
        padding: "0 8px",
        background: "var(--card)",
        border: "1px dashed var(--mod-accent-line)",
        borderRadius: 999,
        color: "var(--mod-accent)",
        fontFamily: "var(--font-mono)",
        fontSize: 10.5,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <StatusIcon kind="hourglass" size={11} />
      <span>{translate("myMachinery:v2.states.awaiting")}</span>
    </span>
  );
};

export default StatusBadge;
